import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Categories tab, top half: the owner's own categories, and moving products.
 *
 * Products are grouped automatically from their names; anything moved here
 * overrides that. `onChanged` lets the icon editor below refresh its list.
 */
function TelegramCategoryManager({ onChanged }) {
    const [cats, setCats] = useState([]);
    const [products, setProducts] = useState([]);
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);
    const [newCat, setNewCat] = useState({ label: '', emoji: '' });
    const [editing, setEditing] = useState(null);   // { oldLabel, label, emoji }
    const [search, setSearch] = useState('');
    const [inCat, setInCat] = useState('');         // '' = all categories
    const [picked, setPicked] = useState(() => new Set());
    const [target, setTarget] = useState('');
    const [shown, setShown] = useState(60);

    const authFetch = useCallback(async (path, options = {}) => {
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        return { ok: res.ok, body: await res.json().catch(() => ({})) };
    }, []);

    const flash = (msg, isError = false) => {
        if (isError) { setError(msg); setNotice(null); } else { setNotice(msg); setError(null); }
        setTimeout(() => { setError(null); setNotice(null); }, 5000);
    };

    const load = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/telegram/category-products');
        if (ok) { setCats(body.categories || []); setProducts(body.products || []); }
    }, [authFetch]);

    useEffect(() => { load(); }, [load]);

    const changed = () => { load(); if (onChanged) onChanged(); };

    const create = async () => {
        const { ok, body } = await authFetch('/admin/telegram/custom-categories', { method: 'POST', body: JSON.stringify(newCat) });
        if (!ok) return flash(body.error || 'Could not create.', true);
        flash(`"${body.label}" created. Move products into it below — an empty category is not shown to buyers.`);
        setNewCat({ label: '', emoji: '' });
        setTarget(body.label);
        changed();
    };

    const saveEdit = async () => {
        const { ok, body } = await authFetch('/admin/telegram/custom-categories', { method: 'PUT', body: JSON.stringify(editing) });
        if (!ok) return flash(body.error || 'Could not save.', true);
        flash('Category saved.');
        setEditing(null);
        changed();
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete "${c.label}"? Its ${c.products} product(s) go back to their automatic category.`)) return;
        const { ok, body } = await authFetch(`/admin/telegram/custom-categories?label=${encodeURIComponent(c.label)}`, { method: 'DELETE' });
        if (!ok) return flash(body.error || 'Could not delete.', true);
        flash(`"${c.label}" deleted.`);
        if (inCat === c.label) setInCat('');
        changed();
    };

    const move = async (ids, label) => {
        if (!ids.length) return flash('Tick the products to move first.', true);
        const { ok, body } = await authFetch('/admin/telegram/category-assign', {
            method: 'PUT', body: JSON.stringify({ ids, label: label || null })
        });
        if (!ok) return flash(body.error || 'Could not move.', true);
        flash(label ? `${body.moved} product(s) moved to "${body.category}".` : `${body.moved} product(s) back to automatic.`);
        setPicked(new Set());
        changed();
    };

    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    const visible = products.filter(p => (!inCat || p.category === inCat) && words.every(w => p.name.toLowerCase().includes(w)));
    const slice = visible.slice(0, shown);
    const allPicked = slice.length > 0 && slice.every(p => picked.has(p.id));
    const togglePick = id => setPicked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const pickAll = () => setPicked(prev => {
        const n = new Set(prev);
        if (allPicked) slice.forEach(p => n.delete(p.id)); else slice.forEach(p => n.add(p.id));
        return n;
    });
    const own = cats.filter(c => c.custom);

    return (
        <div className="tg-card">
            <h3 style={{ marginTop: 0 }}>Your categories &amp; moving products</h3>
            <p className="tg-sub">
                Create your own categories and move any product into any category — including supplier products.
                A moved product stays where you put it; everything else is still sorted automatically by name.
            </p>
            {notice && <div className="tg-note ok">{notice}</div>}
            {error && <div className="tg-note bad">{error}</div>}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
                <input className="tg-input" style={{ width: 70 }} value={newCat.emoji} placeholder="🙂"
                    onChange={e => setNewCat({ ...newCat, emoji: e.target.value })} title="Emoji shown on the button" />
                <input className="tg-input" style={{ flex: '1 1 200px' }} value={newCat.label} placeholder="New category name, e.g. Best Sellers"
                    onChange={e => setNewCat({ ...newCat, label: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') create(); }} />
                <button className="tg-btn primary sm" onClick={create}>+ Create category</button>
            </div>

            {own.length > 0 && (
                <table className="tg-table" style={{ marginBottom: 16 }}>
                    <thead><tr><th>Your category</th><th>Products</th><th></th></tr></thead>
                    <tbody>
                        {own.map(c => (
                            <tr key={c.label}>
                                <td>
                                    {editing && editing.oldLabel === c.label ? (
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <input className="tg-input" style={{ width: 60 }} value={editing.emoji}
                                                onChange={e => setEditing({ ...editing, emoji: e.target.value })} />
                                            <input className="tg-input" value={editing.label}
                                                onChange={e => setEditing({ ...editing, label: e.target.value })}
                                                onKeyDown={e => { if (e.key === 'Enter') saveEdit(); }} />
                                        </div>
                                    ) : <span>{c.emoji} <b>{c.label}</b></span>}
                                </td>
                                <td>{c.products}{c.products === 0 && <span style={{ fontSize: 11, opacity: 0.7 }}> — hidden from buyers until it has products</span>}</td>
                                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                                    {editing && editing.oldLabel === c.label ? (
                                        <>
                                            <button className="tg-btn primary sm" onClick={saveEdit}>Save</button>{' '}
                                            <button className="tg-btn sm" onClick={() => setEditing(null)}>Cancel</button>
                                        </>
                                    ) : (
                                        <>
                                            <button className="tg-btn sm" onClick={() => { setInCat(c.label); setShown(60); }}>Show products</button>{' '}
                                            <button className="tg-btn sm" onClick={() => setEditing({ oldLabel: c.label, label: c.label, emoji: c.emoji || '' })}>Rename</button>{' '}
                                            <button className="tg-btn sm" onClick={() => remove(c)}>Delete</button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <div className="tg-toolbar">
                <input className="tg-search" value={search} onChange={e => { setSearch(e.target.value); setShown(60); }}
                    placeholder={`Search ${products.length} products…`} />
                <select className="tg-input" value={inCat} onChange={e => { setInCat(e.target.value); setShown(60); setPicked(new Set()); }}>
                    <option value="">All categories</option>
                    {cats.map(c => <option key={c.label} value={c.label}>{c.emoji} {c.label} ({c.products})</option>)}
                </select>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '10px 0' }}>
                <span><b>{picked.size}</b> selected — move to</span>
                <select className="tg-input" value={target} onChange={e => setTarget(e.target.value)}>
                    <option value="">Choose a category…</option>
                    {cats.map(c => <option key={c.label} value={c.label}>{c.emoji} {c.label}{c.custom ? ' (yours)' : ''}</option>)}
                </select>
                <button className="tg-btn primary sm" disabled={!target} onClick={() => move([...picked], target)}>Move</button>
                <button className="tg-btn sm" onClick={() => move([...picked], null)}>Reset to automatic</button>
            </div>

            {!visible.length ? <div className="tg-empty">No products here.</div> : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="tg-table">
                        <thead>
                            <tr>
                                <th><input type="checkbox" checked={allPicked} onChange={pickAll} title="Select all shown" /></th>
                                <th>Product</th><th>Category</th><th>Change to</th>
                            </tr>
                        </thead>
                        <tbody>
                            {slice.map(p => (
                                <tr key={p.id}>
                                    <td><input type="checkbox" checked={picked.has(p.id)} onChange={() => togglePick(p.id)} /></td>
                                    <td>
                                        {p.name}
                                        {p.supplier && <span className="tg-tag" style={{ marginLeft: 6 }}>supplier</span>}
                                        {p.soldOut && <span className="tg-tag" style={{ marginLeft: 6, color: '#dc2626' }}>sold out</span>}
                                    </td>
                                    <td>
                                        <b>{p.category}</b>
                                        {p.moved && <div style={{ fontSize: 11, opacity: 0.7 }}>moved by you · automatic: {p.automatic}</div>}
                                    </td>
                                    <td>
                                        <select className="tg-input" value={p.moved ? p.category : ''}
                                            onChange={e => move([p.id], e.target.value || null)}>
                                            <option value="">Automatic ({p.automatic})</option>
                                            {cats.map(c => <option key={c.label} value={c.label}>{c.emoji} {c.label}</option>)}
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {visible.length > shown && (
                <div style={{ textAlign: 'center', marginTop: 10 }}>
                    <button className="tg-btn" onClick={() => setShown(n => n + 60)}>Show more ({visible.length - shown} left)</button>
                </div>
            )}
        </div>
    );
}

export default TelegramCategoryManager;
